import React, { JSX } from 'react';
import { Col, Row } from 'reactstrap';
import { PlusMinus, useConfig, Error, ReactSelectOption } from '@ibe/components';
import { useTranslation } from 'react-i18next';
import Keys from '../../Translations/generated/deal-finder.en.json.keys';
import { observer, useLocalStore } from 'mobx-react';
import { parseInt } from 'lodash-es';
import { ApiRoomContainer } from '@ibe/api';
import { useDeepCompareEffect } from 'react-use';
import { toJS } from 'mobx';
import PaxRoomSelectionController from '../PaxRoomSelection/PaxRoomSelectionController';
import ChildAgePicker from '../PaxRoomSelection/ChildAgePicker';

interface DealFinderPaxSelectorProps {
  initialOccupancy?: ApiRoomContainer[];
  value?: ApiRoomContainer[];
  maxCapacityPerRoom?: number;
  minAdults?: number; // default 1
  maxAdults?: number; // default 10
  maxChildren?: number; // default 10
  maxChildAge?: number; // default 17
  maxCapacity?: number; // default 9
  maxRooms?: number;
  useInitialPersonAmounts?: boolean;
  childAgeRestriction?: number;
  onChange: (o: ApiRoomContainer[]) => void;
  validateChildrenSelection: () => boolean;
  isPaxSelectionNotValid?: boolean;
  isMobile?: boolean;
}

const DealFinderPaxSelector = observer(function DealFinderPaxSelector(
  props: DealFinderPaxSelectorProps
): JSX.Element {
  const {
    initialOccupancy,
    value,
    maxCapacityPerRoom,
    minAdults,
    maxAdults,
    maxChildren,
    maxCapacity,
    maxChildAge,
    maxRooms,
    useInitialPersonAmounts,
    onChange,
    isPaxSelectionNotValid,
    validateChildrenSelection,
    isMobile
  } = props;
  const { t } = useTranslation('deal-finder');
  const config = useConfig();
  // ------ validation states -----------------------------------------------------------------------------

  //------------------------------------------------------------------------------------------------------

  // ------------------------- state init ----------------------------------------------------------------
  const state = useLocalStore(() => new PaxRoomSelectionController());

  state.setMinAdults(minAdults || config.paxRoomConfig.minAdults || 1);
  state.setMaxAdults(
    (maxAdults || config.paxRoomConfig.maxAdults || state.defaultPersonsAmount) + 1
  );
  state.setMaxChildren(
    (maxChildren || config.paxRoomConfig.maxChildren || state.defaultPersonsAmount) + 1
  );
  state.setMaxCapacity(maxCapacity || config.paxRoomConfig.maxCapacity);
  state.setMaxCapacityPerRoom(maxCapacityPerRoom);
  state.setMaxRooms(maxRooms || config.paxRoomConfig.maxRooms);

  useDeepCompareEffect((): void => {
    if (!!value && value.length > 0) {
      state.setRoomCount(value.length);
      state.setRooms([...value]);
      state.setInitialOccupancy({ ...value[0] });
    } else if (!!initialOccupancy && initialOccupancy.length > 0) {
      state.setRoomCount(initialOccupancy.length);
      state.setRooms([...initialOccupancy]);
      state.setInitialOccupancy(initialOccupancy[0]);
    } else {
      state.setRoomCount(1);
      state.triggerRoomCreation();
    }
  }, [value, state, initialOccupancy]);
  // -----------------------------------------------------------------------------------------------------------

  function getMaxAdultsCount(index: number) {
    if (maxCapacityPerRoom) {
      return state.getMaxAdultsLeftPerRoom(index);
    }

    const baseValue = useInitialPersonAmounts ? state.initialMaxAdults : state.getMaxAdults(index);

    return baseValue - 1;
  }

  function getMaxChildrenCount(index: number) {
    if (maxCapacityPerRoom) {
      return state.getMaxChildrenLeftPerRoom(index);
    }

    const baseValue = useInitialPersonAmounts
      ? state.initialMaxChildren
      : state.getMaxChildren(index);

    return baseValue - 1;
  }

  function handleChange() {
    onChange(toJS(state.rooms));
  }

  function buildRoomRow(room: ApiRoomContainer, room_index: number) {
    return (
      <Row key={'roomRow_' + room_index}>
        <Col>
          <Row className="p-2">
            <Col className="align-content-center room-header">{`${t(Keys.room)} ${
              room_index + 1
            }`}</Col>
          </Row>
          <Row className="p-2">
            <Col className="align-content-center">{`${t(Keys.adultsLabel)} ${t(
              Keys.adultAgeRange
            )}`}</Col>
            <Col className="align-content-center">
              <PlusMinus
                label={''}
                currentCount={parseInt(state.getPersonCountOption(room.adults)?.value || '0', 10)}
                onCountChange={(countValue: number): void => {
                  state.setAdultForRoom(room_index, {
                    value: countValue.toString(),
                    label: countValue.toString()
                  });
                  handleChange();
                }}
                minCount={state.getMinAdults()}
                maxCount={getMaxAdultsCount(room_index)}
                validationFailed={
                  state.getAdultCountInRoom(room_index) >= state.getMaxAdultsLeftPerRoom(room_index)
                }
                occupancyErrorMessage={t(Keys.occupancyErrorMessage)}
                plusButtonId={`plus-button-adult-${room_index}`}
              />
            </Col>
          </Row>
          <Row className="p-2">
            <Col className="align-content-center">{`${t(Keys.childrenLabel)} ${t(
              Keys.childAgeRange
            )}`}</Col>
            <Col className="align-content-center">
              <PlusMinus
                label={''}
                currentCount={parseInt(state.getPersonCountOption(room.children)?.value || '0', 10)}
                onCountChange={(countValue: number): void => {
                  state.setChildrenForRoom(room_index, {
                    value: countValue.toString(),
                    label: countValue.toString()
                  });
                  handleChange();
                }}
                minCount={0}
                maxCount={getMaxChildrenCount(room_index)}
                plusButtonId={`plus-button-child-${room_index}`}
                validationFailed={
                  state.getChildrenCountInRoom(room_index) >=
                  state.getMaxChildrenLeftPerRoom(room_index)
                }
                occupancyErrorMessage={t(Keys.occupancyErrorMessage)}
              />
            </Col>
          </Row>
          <Row className="meg_paxRoomSelection__child-age-picker">
            {[...Array(room.children).keys()].map(child_index => (
              <Col xs={12} className="mt-2" key={'childAgePicker_' + child_index}>
                <ChildAgePicker
                  key={child_index}
                  roomIndex={room_index}
                  childIndex={child_index}
                  maxChildAge={maxChildAge}
                  state={state}
                  childAgePlaceholder={t(Keys.select)}
                  validateChildrenSelection={() => {
                    handleChange();
                    return validateChildrenSelection();
                  }}
                  paxHasError={!!isPaxSelectionNotValid}
                  menuPortalTarget={document.getElementById('iso') || undefined}
                  onChange={(
                    roomIndex: number,
                    childIndex: number,
                    newValue: ReactSelectOption
                  ) => {
                    state.setChildAgeForRoom(roomIndex, childIndex, newValue);
                    handleChange();
                  }}
                />
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="meg_deal-finder-pax-selector_container">
      <Col>
        <Row className="px-2 pt-2 pb-1">
          <Col className="align-content-center rooms-header">{t(Keys.roomsLabel)}</Col>
          <Col className="align-content-center">
            <PlusMinus
              label={''}
              currentCount={parseInt(state.roomCount?.value || '0', 10)}
              onCountChange={(countValue: number): void => {
                state.setRoomCountAndTriggerRoomCreation({
                  value: countValue.toString(),
                  label: countValue.toString()
                });
                handleChange();
              }}
              minCount={1}
              maxCount={state.maxRooms - 1}
            />
          </Col>
        </Row>
        <div className="horizontalDivider mx-2 py-1" />
        <Row
          className={`pt-1 pb-2 ${
            isMobile ? 'pax-selector_room-container-mobile' : 'pax-selector_room-container-desktop'
          }`}
        >
          <Col className="align-content-start">
            {state.rooms.map((room, index) => buildRoomRow(room, index))}
          </Col>
        </Row>
        {isPaxSelectionNotValid ? (
          <Row className="pt-2">
            <Col>
              <Error message={t(Keys.validationErrorMessage)} />
            </Col>
          </Row>
        ) : (
          <> </>
        )}
      </Col>
    </Row>
  );
});

export default DealFinderPaxSelector;
